export type RegistrationTiming = {
  endIso?: string;
  registrationClosesAt?: string | null;
  openRegistrationTimeFrame?: boolean;
};

/** Demo rows without timing retain their preview; live rows carry the required end. */
export function registrationClosingInstant(activity: RegistrationTiming): number {
  const end = activity.endIso === undefined ? Infinity : new Date(activity.endIso).getTime();
  const cutoff = activity.openRegistrationTimeFrame || !activity.registrationClosesAt
    ? end : new Date(activity.registrationClosesAt).getTime();
  return Math.min(end, cutoff);
}

export function isRegistrationClosed(activity: RegistrationTiming, now = new Date()): boolean {
  const closing = registrationClosingInstant(activity);
  return Number.isNaN(closing) || now.getTime() >= closing;
}

/** Own one boundary timer and visibility listener; callers release on update/disconnect. */
export function scheduleRegistrationClosingRefresh(
  activities: readonly RegistrationTiming[], refresh: () => void,
): () => void {
  const now = Date.now();
  const next = Math.min(...activities.flatMap((activity) => [
    registrationClosingInstant(activity),
    activity.endIso ? new Date(activity.endIso).getTime() : Infinity,
  ]).filter((at) => Number.isFinite(at) && at > now));
  const timer = Number.isFinite(next)
    ? setTimeout(refresh, Math.min(next - now + 1, 2_147_483_647)) : undefined;
  const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    if (timer !== undefined) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
